import React, { useState } from "react";
import { connect } from "react-redux";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { isEmail } from "validator";

import { StoreState } from "../redux/configure-store";
import { isValidPassword, passwordRequirements } from "../common/utils";
import { submitRegisterForm } from "../authentication/authentication-actions";
import { backgrounds } from "../theme/theme";

const createInitialErrorState = () => ({
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  phoneNumber: "",
});

interface RegisterPageProps {
  isRegisterFailed: boolean;
  submitRegisterForm: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => void;
}

function RegisterPage(props: RegisterPageProps) {
  const { isRegisterFailed, submitRegisterForm } = props;

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState(createInitialErrorState());

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = createInitialErrorState();

    const emptyValueErrorMessage = "This field is required";

    if (data.firstName.length === 0) {
      newErrors.firstName = emptyValueErrorMessage;
    }

    if (data.lastName.length === 0) {
      newErrors.lastName = emptyValueErrorMessage;
    }

    if (!isEmail(data.email)) newErrors.email = "Please enter a valid email";

    if (!isValidPassword(data.password))
      newErrors.password = "Password does not meet requirements";

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some(
      (error: string) => error.length !== 0
    );

    if (!hasErrors) {
      submitRegisterForm(
        data.email,
        data.password,
        data.firstName,
        data.lastName
      );
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: backgrounds.page,
        py: 4,
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 480,
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "primary.main",
              mb: 2,
            }}
          >
            <PersonAddIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Typography component="h1" variant="h5" fontWeight={600}>
            Create New User
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add a new user to the system
          </Typography>
        </Box>

        {isRegisterFailed && (
          <Alert severity="error" sx={{ mb: 3 }}>
            User already registered or an error occurred. Please try again.
          </Alert>
        )}

        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="firstName"
                name="firstName"
                label="First Name"
                autoFocus
                autoComplete="off"
                value={data.firstName}
                onChange={onChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                id="lastName"
                name="lastName"
                label="Last Name"
                autoComplete="off"
                value={data.lastName}
                onChange={onChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="email"
                name="email"
                label="Email Address"
                type="email"
                autoComplete="off"
                value={data.email}
                onChange={onChange}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="password"
                name="password"
                label="Password"
                type="password"
                autoComplete="off"
                value={data.password}
                onChange={onChange}
                error={!!errors.password}
                helperText={errors.password || passwordRequirements}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            Create User
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  isRegisterFailed: storeState.authentication.registerFailed,
});

export default connect(mapStateToProps, { submitRegisterForm })(RegisterPage);
